import { useEffect, useMemo, useRef, useState } from 'react';
import { getYahooState, startYahooScan, setYahooHandled } from '../api/yahooScan';

// Quét hạn đơn Yahoo: TRƯỞNG PHÒNG/GĐ bấm quét (chạy nền, quyền GĐ = full đơn) -> lưu kết quả.
// Mọi người xem: NV thấy đơn của mình, TP/GĐ thấy tất cả. Hiện TOÀN BỘ đơn Yahoo "Chờ báo giá"
// + cảnh báo đơn cần liên hệ/thanh toán sớm (đẩy lên đầu). Tách cột Khách / Sale + lọc theo sale.
const fmt = (d) => {
  if (!d) return '';
  try {
    return new Date(d).toLocaleString('vi-VN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' });
  } catch {
    return '';
  }
};
const fmtDay = (d) => {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' });
  } catch {
    return '';
  }
};
// QUÁ HẠN: đơn có NGÀY trước hôm nay (vd hôm nay 17/8 -> đơn 16/8 trở về trước) -> bôi đỏ cảnh báo.
const isOverdue = (d) => {
  if (!d) return false;
  const dt = new Date(d);
  if (isNaN(dt)) return false;
  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  return dt.getTime() < startToday;
};

function DeadlineCell({ it, onCopy }) {
  if (it.warn) {
    return (
      <div className="row" style={{ gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        {(it.deadlines || []).map((d, i) => (
          <span key={i} className="chip" style={{ background: d.hours != null && d.hours <= 24 ? 'var(--danger)' : 'var(--warn, #d97706)', color: '#fff', fontSize: 12, fontWeight: 600 }}>{d.vi}</span>
        ))}
        <button className="btn sm ghost" onClick={() => onCopy(it)} title="Copy nội dung hạn để dán lên SocSon">Copy</button>
      </div>
    );
  }
  if (!it.ok) return <span className="small muted">⚠️ chặn/gỡ — kiểm tra tay</span>;
  return <span className="muted">—</span>;
}

// Ô trạng thái xử lý — bấm để đổi Chưa xử lý <-> Đã xử lý.
function HandledCell({ it, onToggle }) {
  return (
    <button
      className={'chip ' + (it.handled ? 'done' : 'todo')}
      style={{ cursor: 'pointer', border: 'none', fontWeight: 600 }}
      title={it.handled ? `Đã xử lý${it.handledBy ? ' bởi ' + it.handledBy : ''}${it.handledAt ? ' · ' + fmt(it.handledAt) : ''} — bấm để bỏ` : 'Bấm để đánh dấu ĐÃ XỬ LÝ'}
      onClick={() => onToggle(it)}
    >
      {it.handled ? '✓ Đã xử lý' : 'Chưa xử lý'}
    </button>
  );
}

export default function YahooScanPage() {
  const [state, setState] = useState(null);
  const [err, setErr] = useState('');
  const [starting, setStarting] = useState(false);
  const [saleFilter, setSaleFilter] = useState('');
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const d = await getYahooState();
      setState(d);
      return d;
    } catch (e) {
      setErr(e?.response?.data?.message || 'Không tải được trạng thái (backend cần đang chạy).');
      return null;
    }
  };

  useEffect(() => {
    load();
    return () => clearInterval(pollRef.current);
  }, []);

  // Đang quét -> poll mỗi 2.5s cho tới khi xong.
  useEffect(() => {
    clearInterval(pollRef.current);
    if (state?.status === 'running') pollRef.current = setInterval(load, 2500);
    return () => clearInterval(pollRef.current);
  }, [state?.status]);

  const scan = async () => {
    setStarting(true);
    setErr('');
    try {
      const r = await startYahooScan();
      if (r?.running || r?.tooEarly) setErr(r.message || 'Đang có người quét, chờ chút…');
      await load();
    } catch (e) {
      setErr(e?.response?.data?.message || 'Quét thất bại.');
    } finally {
      setStarting(false);
    }
  };

  const copyNote = (it) => {
    const txt = 'HẠN: ' + (it.deadlines || []).map((d) => d.vi).join(' · ');
    navigator.clipboard?.writeText(txt).catch(() => {});
  };

  // Đổi trạng thái đã/chưa xử lý — cập nhật ngay (optimistic), lỗi thì tải lại.
  const toggleHandled = async (it) => {
    const next = !it.handled;
    setState((s) => ({ ...s, items: (s.items || []).map((x) => (x.code === it.code ? { ...x, handled: next, handledBy: next ? 'Bạn' : '', handledAt: next ? new Date().toISOString() : null } : x)) }));
    try {
      await setYahooHandled(it.code, next);
    } catch (e) {
      setErr(e?.response?.data?.message || 'Không đổi được trạng thái.');
      load();
    }
  };

  const running = state?.status === 'running';
  const canScan = !!state?.canScan;

  // Danh sách sale có trong kết quả (để đổ vào dropdown lọc).
  const sales = useMemo(() => {
    const set = new Set((state?.items || []).map((i) => i.sale).filter(Boolean));
    return [...set].sort((a, b) => a.localeCompare(b, 'vi'));
  }, [state?.items]);

  // CHỈ hiện đơn CÓ cảnh báo (liên hệ/thanh toán). Đơn không cảnh báo -> ẩn hẳn.
  const items = (state?.items || []).filter((i) => i.warn).filter((i) => (saleFilter ? i.sale === saleFilter : true));
  const shownPending = items.filter((i) => !i.handled).length;
  const shownOverdue = items.filter((i) => isOverdue(i.createdDate)).length;

  return (
    <div className="page">
      <div className="page-head" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>⏰ Quét hạn đơn Yahoo</h2>
        {canScan && (
          <button className="btn" disabled={running || starting} onClick={scan}>
            {running ? 'Đang quét…' : starting ? 'Đang bắt đầu…' : '🔍 Quét đơn hàng ngày'}
          </button>
        )}
      </div>

      <div className="small muted" style={{ marginBottom: 10, lineHeight: 1.7 }}>
        Quét các đơn <b>đấu giá Yahoo đang "Chờ báo giá"</b>, đọc mô tả seller và <b>cảnh báo hạn liên hệ / thanh toán</b>
        {' '}(vd "liên hệ 12h", "thanh toán 3 ngày") để <b>note lên SocSon</b> kịp. Đơn đã quét được lưu lại — lần quét sau <b>chỉ tải đơn mới</b>.{' '}
        <b>Hệ thống tự quét 6h sáng</b>; {canScan ? 'trưởng phòng bấm "Quét đơn hàng ngày" để quét thêm đơn mới sau 8h.' : 'trưởng phòng quét thêm sau 8h — bạn xem đơn của mình.'}
      </div>

      {/* Lần quét gần nhất: ai + lúc nào */}
      <div className="card" style={{ marginBottom: 12 }}>
        {running ? (
          <>
            🔄 <b>Đang quét</b>{state?.byName ? <> bởi <b>{state.byName}</b></> : null} — đã quét <b>{state?.scanned || 0}</b>/<b>{state?.total || 0}</b> đơn…
          </>
        ) : state?.startedAt || state?.finishedAt ? (
          <>
            🕒 Lần quét gần nhất: bởi <b>{state?.byName || '—'}</b> lúc <b>{fmt(state?.finishedAt || state?.startedAt)}</b>
            {state?.status === 'error' ? <span style={{ color: 'var(--danger)' }}> · (lần quét trước bị lỗi/gián đoạn){state?.error ? ` — ${state.error}` : ''}</span> : null}
            {' · '}Đơn Yahoo "Chờ báo giá": <b>{state?.total || 0}</b>
          </>
        ) : (
          <>Chưa có lần quét nào. {canScan ? 'Bấm "Quét đơn hàng ngày" để bắt đầu.' : 'Chờ trưởng phòng quét.'}</>
        )}
      </div>

      {err && <div className="card" style={{ color: 'var(--danger)', marginBottom: 12 }}>{err}</div>}

      {/* Bộ lọc */}
      {state && (
        <div className="row" style={{ gap: 12, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          {sales.length > 1 && (
            <label className="small" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              Lọc theo sale:
              <select value={saleFilter} onChange={(e) => setSaleFilter(e.target.value)} className="input" style={{ minWidth: 160 }}>
                <option value="">Tất cả sale ({sales.length})</option>
                {sales.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </label>
          )}
          <span className="small muted">Đơn có hạn: <b>{items.length}</b> · <b>{shownPending}</b> chưa xử lý · <b style={{ color: 'var(--danger)' }}>{shownOverdue}</b> quá hạn</span>
        </div>
      )}

      {/* DESKTOP: bảng tách cột Khách / Sale riêng */}
      {items.length > 0 && (
        <div className="only-desktop">
          <div className="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th>Mã đơn</th>
                <th>Khách hàng</th>
                <th>Sale</th>
                <th className="c">Trạng thái</th>
                <th className="c">Ngày</th>
                <th>Hạn / Cảnh báo</th>
                <th className="c">Xử lý</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const overdue = !it.handled && isOverdue(it.createdDate);
                return (
                <tr key={it.code} style={{ ...(overdue ? { background: 'rgba(220,38,38,.16)' } : !it.handled ? { background: 'rgba(220,38,38,.05)' } : {}), ...(it.handled ? { opacity: 0.55 } : {}) }}>
                  <td>
                    <b>{it.code}</b>
                    <div><a href={it.link} target="_blank" rel="noreferrer" className="small" style={{ color: 'var(--accent)' }}>🔗 Yahoo</a></div>
                  </td>
                  <td>{it.customer || <span className="muted">—</span>}</td>
                  <td>{it.sale || <span className="muted">—</span>}</td>
                  <td className="c">{it.status ? <span className="chip" style={{ fontSize: 11 }}>{it.status}</span> : '—'}</td>
                  <td className="c small" style={overdue ? { color: 'var(--danger)', fontWeight: 700 } : { color: 'var(--muted)' }} title={overdue ? 'Quá hạn — đơn để quá 1 ngày' : undefined}>
                    {overdue ? '⚠️ ' : ''}{fmtDay(it.createdDate)}
                  </td>
                  <td><DeadlineCell it={it} onCopy={copyNote} /></td>
                  <td className="c"><HandledCell it={it} onToggle={toggleHandled} /></td>
                </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* MOBILE: card, Khách và Sale trên 2 dòng riêng */}
      {items.length > 0 && (
        <div className="only-mobile">
          {items.map((it) => {
            const overdue = !it.handled && isOverdue(it.createdDate);
            return (
            <div key={it.code} className="card" style={{ marginBottom: 8, borderLeft: '3px solid ' + (it.handled ? 'transparent' : 'var(--danger)'), background: overdue ? 'rgba(220,38,38,.10)' : undefined, opacity: it.handled ? 0.6 : 1 }}>
              <div className="row between" style={{ flexWrap: 'wrap', gap: 6 }}>
                <b>{it.code}</b>
                {it.status ? <span className="chip" style={{ fontSize: 11 }}>{it.status}</span> : null}
              </div>
              <div className="small" style={{ marginTop: 2 }}>Khách: <b>{it.customer || '—'}</b></div>
              <div className="small">Sale: <b>{it.sale || '—'}</b> · <span style={overdue ? { color: 'var(--danger)', fontWeight: 700 } : undefined}>{overdue ? '⚠️ ' : ''}{fmtDay(it.createdDate)}</span></div>
              <a href={it.link} target="_blank" rel="noreferrer" className="small" style={{ color: 'var(--accent)', wordBreak: 'break-all' }}>{it.link}</a>
              <div className="row between" style={{ marginTop: 6, gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <DeadlineCell it={it} onCopy={copyNote} />
                <HandledCell it={it} onToggle={toggleHandled} />
              </div>
            </div>
            );
          })}
        </div>
      )}

      {state && !items.length && state.status !== 'running' && (
        <div className="card">{saleFilter ? 'Không đơn cần chú ý nào của sale này.' : '✅ Không có đơn nào cần liên hệ/thanh toán (hoặc chưa quét).'}</div>
      )}
    </div>
  );
}
